import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Button, Stack, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

const StyledFooter = styled(Box)(({ theme }) => ({
  backgroundColor: '#1976d2',
  color: 'white',
  position: 'fixed',
  bottom: 0,
  width: '100%',
  height: '1.9rem',
  zIndex: 100,
  '@media (max-width: 600px)': {
    padding: theme.spacing(0.5, 0),
    height: '3rem',
  }
}));

const FooterButton = styled(Button)({
  color: 'white',
  padding: '4px 8px',
  minWidth: 'auto',
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  '@media (max-width: 600px)': {
    fontSize: '0.75rem',
    padding: '2px 6px',
  }
});

const ModalContainer = styled(Box)({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
  padding: '24px',
  width: '400px',
  maxWidth: '90%',
  maxHeight: '70vh',
  overflowY: 'auto',
  zIndex: 1002,
  opacity: 0,
  visibility: 'hidden',
  transition: 'all 0.3s ease-in-out',
  '&.visible': {
    opacity: 1,
    visibility: 'visible',
  }
});

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: '8px',
  right: '8px',
  color: '#1976d2',
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.1)',
  }
});

const ModalLinkButton = styled(Button)({
  width: '100%',
  marginBottom: '12px',
  padding: '14px 20px',
  backgroundColor: '#1976d2',
  color: 'white',
  borderRadius: '8px',
  fontSize: '0.95rem',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  justifyContent: 'flex-start',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#1565c0',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
  },
  '&:last-child': {
    marginBottom: 0,
  },
  transition: 'all 0.2s ease',
});

// Custom colored buttons for contact options
const EmailButton = styled(ModalLinkButton)({
  backgroundColor: '#EA4335',
  '&:hover': {
    backgroundColor: '#D33426',
    boxShadow: '0 4px 12px rgba(234, 67, 53, 0.3)',
  }
});

const WhatsAppButton = styled(ModalLinkButton)({
  backgroundColor: '#25D366',
  '&:hover': {
    backgroundColor: '#1DA851',
    boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
  }
});

const TelegramButton = styled(ModalLinkButton)({
  backgroundColor: '#0088cc',
  '&:hover': {
    backgroundColor: '#006699',
    boxShadow: '0 4px 12px rgba(0, 136, 204, 0.3)',
  }
});

const Overlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1001,
  opacity: 0,
  visibility: 'hidden',
  transition: 'opacity 0.3s ease-in-out',
  '&.visible': {
    opacity: 1,
    visibility: 'visible',
  }
});

const ModalTitle = styled('h2')({
  textAlign: 'center',
  marginBottom: '24px',
  color: '#1976d2',
  fontSize: '1.5rem',
  fontWeight: 600,
  margin: '0 0 24px 0',
});

const Footer = () => {
  const [showGehuLinks, setShowGehuLinks] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showJoinUsModal, setShowJoinUsModal] = useState(false);

  const handleContactClick = (e) => {
    e.preventDefault();
    setShowContactModal(true);
  };

  const handleCloseContactModal = (e) => {
    if (e) e.preventDefault();
    setShowContactModal(false);
  };

  const handleGehuLinksClick = (e) => {
    e.preventDefault();
    setShowGehuLinks(true);
  };

  const handleCloseGehuLinks = (e) => {
    if (e) e.preventDefault();
    setShowGehuLinks(false);
  };

  const handleJoinUsClick = (e) => {
    e.preventDefault();
    setShowJoinUsModal(true);
  };

  const handleCloseJoinUsModal = (e) => {
    if (e) e.preventDefault();
    setShowJoinUsModal(false);
  };

  const gehuLinks = [
    { name: 'GEHU Website', url: 'https://www.gehu.ac.in' },
    { name: 'GEHU ERP', url: 'https://student.gehu.ac.in' },
    { name: 'GEU WEBSITE', url: 'https://geu.ac.in/' },
    { name: 'NOTICE', url: 'http://btechcsegehu.in/notices-2/' },
    { name: 'EXAM PORTAL', url: 'https://gehu.ac.in/dehradun/exam-portal/' },
    { name: 'GEHU B.Tech CSE', url: 'http://btechcsegehu.in' },
    { name: 'GEHU B.Tech First Year', url: 'https://btechgehu.in' },
    { name: 'GEHU Exam Portal', url: 'https://exam.gehu.ac.in' },
    { name: 'GEU ERP', url: 'https://student.geu.ac.in' },
    { name: 'GEU Website', url: 'https://geu.ac.in' },
    { name: 'GEU B.Tech CSIT', url: 'https://csitgeu.in/wp' },
    { name: 'GEU B.Tech First Year', url: 'https://btechgeu.in/wp24' }
  ];

  const joinUsOptions = [
    {
      name: 'Instagram (QuickRoll)',
      icon: <InstagramIcon />,
      url: 'https://www.instagram.com/quickrollattendance',
      color: '#E4405F'
    },
    {
      name: 'LinkedIn (QuickRoll)',
      icon: <LinkedInIcon />,
      url: 'https://www.linkedin.com/company/quickrollattendance',
      color: '#0A66C2'
    },
    {
      name: 'Instagram (BTech CSE, GEHU)',
      icon: <InstagramIcon />,
      url: 'https://www.instagram.com/gehu_cse?igsh=MWtkMTIxYTYwYXB4cA==',
      color: '#E4405F'
    },
    {
      name: 'LinkedIn (BTech CSE, GEHU)',
      icon: <LinkedInIcon />,
      url: 'https://www.linkedin.com/in/department-of-computer-science-and-engineering-gehu-dehradun-b998a0377',
      color: '#0A66C2'
    }
  ];

  const contactOptions = [
    {
      name: 'Email Us',
      icon: <EmailIcon />,
      action: () => {
        window.location.href = `mailto:${process.env.REACT_APP_ADMIN_EMAIL}?subject=Contact%20Request&body=Hello%20Admin,%0A%0A`;
      },
      ButtonComponent: EmailButton
    },
    {
      name: 'WhatsApp Community',
      icon: <WhatsAppIcon />,
      action: () => {
        window.open('https://chat.whatsapp.com/KG8CtSiIjwzJgZTDadQ5VE', '_blank');
      },
      ButtonComponent: WhatsAppButton
    },
    {
      name: 'Telegram Community',
      icon: <TelegramIcon />,
      action: () => {
        window.open('https://t.me/quickrollattendance', '_blank');
      },
      ButtonComponent: TelegramButton
    }
  ];

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowGehuLinks(false);
        setShowContactModal(false);
        setShowJoinUsModal(false);
      }
    };

    if (showGehuLinks || showContactModal || showJoinUsModal) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showGehuLinks, showContactModal, showJoinUsModal]);

  return (
    <>
      <StyledFooter>
        <Container maxWidth="lg">
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            alignItems="center"
            height="100%"
            sx={{ pb: 3 }}
          >
            <FooterButton component={Link} to="/">
              Home
            </FooterButton>
            <FooterButton component={Link} to="/about">
              About Us
            </FooterButton>
            <FooterButton onClick={handleContactClick}>
              Contact Us
            </FooterButton>
            <FooterButton onClick={handleJoinUsClick}>
              Join Us
            </FooterButton>
            <FooterButton onClick={handleGehuLinksClick}>
              GEHU LINKS
            </FooterButton>
          </Stack>
        </Container>
      </StyledFooter>

      {/* Overlay for modals */}
      <Overlay
        className={showGehuLinks || showContactModal || showJoinUsModal ? 'visible' : ''}
        onClick={() => {
          handleCloseGehuLinks();
          handleCloseContactModal();
          handleCloseJoinUsModal();
        }}
      />

      {/* GEHU Links Modal */}
      <ModalContainer className={showGehuLinks ? 'visible' : ''}>
        <CloseButton
          onClick={handleCloseGehuLinks}
          aria-label="close"
        >
          <CloseIcon />
        </CloseButton>
        <ModalTitle>GEHU Links</ModalTitle>
        {gehuLinks.map((link, index) => (
          <ModalLinkButton
            key={index}
            onClick={() => {
              window.open(link.url, '_blank');
              setShowGehuLinks(false);
            }}
          >
            {link.name}
          </ModalLinkButton>
        ))}
      </ModalContainer>

      {/* Contact Us Modal */}
      <ModalContainer className={showContactModal ? 'visible' : ''}>
        <CloseButton
          onClick={handleCloseContactModal}
          aria-label="close"
        >
          <CloseIcon />
        </CloseButton>
        <ModalTitle>Contact Us</ModalTitle>
        {contactOptions.map((option, index) => {
          const ButtonComponent = option.ButtonComponent;
          return (
            <ButtonComponent
              key={index}
              onClick={() => {
                option.action();
                setShowContactModal(false);
              }}
            >
              {option.icon}
              {option.name}
            </ButtonComponent>
          );
        })}
      </ModalContainer>

      {/* Join Us Modal */}
      <ModalContainer className={showJoinUsModal ? 'visible' : ''}>
        <CloseButton
          onClick={handleCloseJoinUsModal}
          aria-label="close"
        >
          <CloseIcon />
        </CloseButton>
        <ModalTitle>Join Us</ModalTitle>
        {joinUsOptions.map((option, index) => (
          <ModalLinkButton
            key={index}
            sx={{ backgroundColor: option.color, '&:hover': { backgroundColor: option.color, filter: 'brightness(0.9)' } }}
            onClick={() => {
              window.open(option.url, '_blank');
              setShowJoinUsModal(false);
            }}
          >
            {option.icon}
            {option.name}
          </ModalLinkButton>
        ))}
      </ModalContainer>
    </>
  );
};

export default Footer;